import React, { useCallback, useEffect, useRef, useState } from "react";
import { ScrollToFirstError, scrollToFirstErrorSoon } from "../../../Shared/scrollToFirstError";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Alert, Button, Chip, CircularProgress, IconButton, LinearProgress, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { AccountBalanceOutlined, DownloadOutlined, RefreshOutlined } from "@mui/icons-material";
import { DialogFrame } from "../BriskCertificateList/BriskV2Dialogs";
import FormSection from "../OnlinePolicy/components/FormSection";
import { CHOICE_TOGGLE_SX, FONT, NAVY, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from "../OnlinePolicy/components/formStyles";
import "../OnlinePolicy/components/onlinePolicyForm.scss";
import { Formik, Form, Field } from "formik";
import { DatePicker } from "antd";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useLocation, useNavigate } from "react-router-dom";
import swal from "../../../Shared/swal";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
import { isSafeLink } from "../BriskCertificateList/briskV2ListUtils";
import { AccountBalanceWalletIcon } from "../../../Resources/Icons/icons";
import { PAYMENT_DETAILS, POLICY_TYPE_DETAILS } from "../../../Shared/CommonConstant";
import { createAttemptTracker, formatINR, shouldReuseRequestId } from "../../AddMoneyToUser/addMoneyUtils";
import { BRISK_V2_LIST_PATH } from "../../../Shared/briskV2Flag";
import {
  createBriskV2Certificate,
  getBriskV2Balance,
  getBriskV2DealerBalance,
  getBriskV2Certificate,
  getBriskV2Config,
  getBriskV2Details,
  getBriskV2DownloadUrl,
  getBriskV2Lists,
  getBriskV2Models,
  getBriskV2PendingPolicies,
  getBriskV2PendingPolicy,
  saveBriskV2Draft,
  updateBriskV2Certificate,
} from "../../../Service/BriskV2Service";
import {
  MODE_NEW,
  MODE_POLICY,
  UPDATE_LOCKED_FIELDS,
  attemptFingerprint,
  buildSchema,
  dobForPicker,
  formToValues,
  initialValues,
  policyGivesPremium,
  policyOptionLabel,
  splitPremium,
  valuesToForm,
} from "./briskV2FormUtils";

/**
 * Brisk Create Certificate — the new page behind /briskCreateCertificate (REACT_APP_BRISK_V2_ENABLED). Everything goes
 * through the backend's /briskV2 routes, which talk to Brisk's certificate API (TEST now, LIVE by the backend .env).
 *
 * Same sections, labels and buttons as the old page (Policy/BriskCreateCertificate, kept as the "switch off" page),
 * plus what the owner asked for: "New policy / Already created policy" (a policy already paid for its PA is never
 * charged again), Brisk's own lists, the Company Wallet "Brisk" balance, a confirmation when Brisk already knows the
 * engine or chassis, and a success dialog with a real Download link.
 *
 * The look is the Online Policy form's (the owner, 2026-10-31): section cards, the same one-of-many switch, the buttons in
 * a bar stuck to the bottom of the window (.op-form.op-policy in onlinePolicyForm.scss).
 */

const labelSx = { mb: 0.5, fontSize: { xs: "0.85rem", sm: "0.9rem" }, fontWeight: 500 };
const hintSx = { fontFamily: FONT, fontSize: 12, color: "#667085" };
const BALANCE_SX = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  height: 36,
  px: 1.5,
  borderRadius: "8px",
  border: "1px solid #D6E1EE",
  backgroundColor: "#EEF3F9",
  color: NAVY,
  fontFamily: FONT,
  fontSize: "13px",
  fontWeight: 600,
  "& svg": { fontSize: 18 },
};
const BAR_BUTTON_SX = { height: 38, fontSize: "13px", minWidth: { xs: 104, sm: 140 } };

const Label = ({ children, optional = false }) => (
  <Typography sx={labelSx}>
    {children} {!optional && <span style={{ color: "red" }}>*</span>}
  </Typography>
);

const FieldError = ({ name, errors, touched }) => (
  <div className="errorMessage mb-2 mt-2">{errors[name] && touched[name] ? <div>{errors[name]}</div> : " "}</div>
);

const toOptions = (list) => (list || []).map((v) => ({ label: v, value: v }));

const BriskCreateCertificateV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const formElRef = useRef(null); // the <form>: the policy check made after Formik's scrolls to its message too
  const actionRef = useRef("create");
  const trackerRef = useRef(createAttemptTracker());
  // A ref, not state: two clicks in the same moment both see the old state, but never the old ref.
  const sendingRef = useRef(false);

  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState("");
  const [lists, setLists] = useState({ genders: [], relations: [], makes: [], plans: [] });
  const [listsError, setListsError] = useState("");
  const [models, setModels] = useState([]);
  const [balance, setBalance] = useState(null);
  const [dealerBalance, setDealerBalance] = useState(null);
  const [mode, setMode] = useState(MODE_NEW);
  const [pendingPolicies, setPendingPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyError, setPolicyError] = useState("");
  const [fillDOB, setFillDOB] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [updateId, setUpdateId] = useState(null);
  const [sending, setSending] = useState(false);
  // Reading a policy or a certificate into the form: a thin bar, never a loader over the page.
  const [reading, setReading] = useState(false);
  // What is being sent: "create" | "update" | "draft". The spinner sits on that button.
  const [sendingAction, setSendingAction] = useState("");
  const [success, setSuccess] = useState(null);

  const isUpdate = Boolean(updateId);
  const can = (config && config.can) || {};
  const readOnly = !config || !can.create;

  const loadBalance = useCallback(async () => {
    const res = await getBriskV2Balance();
    if (res.error) {
      setBalance(null);
      return;
    }
    setBalance(res.data && res.data.hasWallet ? res.data.balance : null);
  }, []);

  // Brisk's own (dealer) wallet — asked only when the server says this login may see it (BRISK_V2_DEALER_BALANCE_FOR).
  const loadDealerBalance = useCallback(async () => {
    const res = await getBriskV2DealerBalance();
    setDealerBalance(res.error || !res.data ? null : res.data.balance);
  }, []);

  const refreshBalances = () => {
    loadBalance();
    if (can.seeDealerBalance) loadDealerBalance();
  };

  const loadModels = useCallback(async (make) => {
    if (!make) {
      setModels([]);
      return;
    }
    const res = await getBriskV2Models(make);
    setModels(res.error ? [] : res.data || []);
  }, []);

  // Config, Brisk's lists and the wallet, once.
  useEffect(() => {
    let alive = true;
    (async () => {
      const cfg = await getBriskV2Config();
      if (!alive) return;
      if (cfg.error) {
        setConfigError(cfg.message || "Brisk certificates are not available");
        return;
      }
      setConfig(cfg.data);
      if (cfg.data && cfg.data.problem) setConfigError(cfg.data.problem);
      const seesDealer = Boolean(cfg.data && cfg.data.can && cfg.data.can.seeDealerBalance);
      const [l] = await Promise.all([getBriskV2Lists(), loadBalance(), seesDealer ? loadDealerBalance() : null]);
      if (!alive) return;
      if (l.error) setListsError(l.message || "Could not load Brisk's lists. Please refresh the page");
      else setLists(l.data);
    })();
    return () => {
      alive = false;
    };
  }, [loadBalance, loadDealerBalance]);

  const fillForm = useCallback(
    (form) => {
      const values = formToValues(form);
      if (formRef.current) formRef.current.setValues(values, false);
      setFillDOB(dobForPicker(values.CustomerDOB));
      loadModels(values.Make);
    },
    [loadModels]
  );

  const choosePolicy = useCallback(
    async (policyId) => {
      setPolicyError("");
      if (!policyId) {
        setSelectedPolicy(null);
        return;
      }
      setReading(true);
      const res = await getBriskV2PendingPolicy(policyId);
      setReading(false);
      if (res.error) {
        setSelectedPolicy(null);
        setPolicyError(res.message);
        return;
      }
      setSelectedPolicy({ ...res.data.policy, paAmount: res.data.paAmount, briskCharged: res.data.briskCharged, chargedAgain: res.data.chargedAgain });
      fillForm(res.data.form);
      return res.data;
    },
    [fillForm]
  );

  // Where the page was opened from: an Online Policy (its "Create Certificate" button), a draft, or Edit.
  useEffect(() => {
    const state = location.state || {};
    const v2 = state.briskV2 || {};
    setSuccess(null);
    trackerRef.current.reset();
    if (v2.mode === "update" && v2.certificateId) {
      setUpdateId(v2.certificateId);
      (async () => {
        setReading(true);
        const res = await getBriskV2Details(v2.certificateId);
        setReading(false);
        if (res.error) ToastError(res.message);
        else fillForm(res.data.form);
      })();
    } else if (v2.mode === "draft" && v2.certificateId) {
      (async () => {
        const res = await getBriskV2Certificate(v2.certificateId);
        if (res.error) {
          ToastError(res.message);
          return;
        }
        setDraftId(v2.certificateId);
        let form = res.data.form;
        if (res.data.source && res.data.source.kind === MODE_POLICY && res.data.source.onlinePolicyId) {
          setMode(MODE_POLICY);
          const policy = await choosePolicy(res.data.source.onlinePolicyId);
          // a draft saved earlier may hold a typed amount: the policy's is the one that counts
          if (policy && policyGivesPremium(policy.paAmount)) form = { ...form, totalPremium: policy.form.totalPremium };
        }
        fillForm(form);
      })();
    } else if (state.prefillFromPolicy && state.onlinePolicyId) {
      // Old editMode / oldCertificateId are ignored on purpose: an old certificate is never deactivated from here.
      setMode(MODE_POLICY);
      choosePolicy(state.onlinePolicyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // The policies whose Brisk PA failed (the server decides), when "Already created policy" is chosen.
  useEffect(() => {
    if (mode !== MODE_POLICY || !config) return;
    let alive = true;
    getBriskV2PendingPolicies().then((res) => {
      if (!alive) return;
      if (res.error) setPolicyError(res.message);
      else setPendingPolicies(res.data || []);
    });
    return () => {
      alive = false;
    };
  }, [mode, config]);

  const switchMode = (next) => {
    setMode(next);
    setSelectedPolicy(null);
    setPolicyError("");
    setDraftId(null);
    trackerRef.current.reset();
    if (formRef.current) formRef.current.resetForm({ values: initialValues });
    setFillDOB(null);
  };

  const createNow = async (values, confirmRegistered) => {
    const onlinePolicyId = mode === MODE_POLICY && selectedPolicy ? selectedPolicy.id : undefined;
    if (mode === MODE_POLICY && !onlinePolicyId) {
      setPolicyError("Please choose the policy");
      ToastError("Please choose the policy");
      scrollToFirstErrorSoon(formElRef.current);
      return;
    }
    const requestId = trackerRef.current.idFor({ userId: attemptFingerprint(values, mode, onlinePolicyId), amount: values.totalPremium });
    const res = await createBriskV2Certificate({
      mode,
      onlinePolicyId,
      requestId,
      draftId: draftId || undefined,
      form: valuesToForm(values),
      confirmRegistered,
    });
    if (!res.error) {
      trackerRef.current.reset();
      setSuccess({ message: res.message, certificateNo: res.data.certificateNo, url: res.data.url, id: res.data.id });
      refreshBalances();
      return;
    }
    if (!shouldReuseRequestId(res)) trackerRef.current.reset();
    if (res.code === "NEEDS_CONFIRMATION") {
      const yes = await swal({
        title: "Already registered with Brisk",
        text: res.message,
        icon: "warning",
        buttons: ["No", "Yes, create it"],
        dangerMode: true,
      });
      if (yes) await createNow(values, { engineNo: res.data.engineNo, chassisNo: res.data.chassisNo });
      return;
    }
    if (res.code === "UNKNOWN_OUTCOME" || res.code === "IN_PROGRESS") {
      swal({ title: "Not known yet", text: res.message, icon: "warning" });
      refreshBalances();
      return;
    }
    ToastError(res.message);
    refreshBalances();
  };

  const onSubmit = async (values) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    const action = actionRef.current;
    setSendingAction(action);
    try {
      if (action === "draft") {
        const res = await saveBriskV2Draft({
          draftId: draftId || undefined,
          mode,
          onlinePolicyId: selectedPolicy ? selectedPolicy.id : undefined,
          form: valuesToForm(values),
        });
        if (res.error) ToastError(res.message);
        else {
          setDraftId(res.data.id);
          ToastSuccess(res.message || "Draft saved");
        }
      } else if (action === "update") {
        const res = await updateBriskV2Certificate(updateId, { form: valuesToForm(values) });
        if (res.error) ToastError(res.message);
        else {
          await swal({ title: "Certificate updated", text: res.message, icon: "success" });
          navigate(BRISK_V2_LIST_PATH);
        }
      } else {
        await createNow(values);
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
      setSendingAction("");
    }
  };

  const startAnother = () => {
    setSuccess(null);
    setDraftId(null);
    setSelectedPolicy(null);
    if (formRef.current) formRef.current.resetForm({ values: initialValues });
    setFillDOB(null);
    if (mode === MODE_POLICY) {
      getBriskV2PendingPolicies().then((res) => !res.error && setPendingPolicies(res.data || []));
    }
  };

  const premiumSplit = (values) => splitPremium(values.totalPremium);
  const lockedInUpdate = (name) => isUpdate && UPDATE_LOCKED_FIELDS.includes(name);
  const newBlockedForSuperAdmin = mode === MODE_NEW && config && !can.createNew;
  // Already created policy: its PA amount is given, so the Total Premium is not typed (the owner, 2026-10-31; the server checks too).
  const premiumFromPolicy = mode === MODE_POLICY && (!selectedPolicy || policyGivesPremium(selectedPolicy.paAmount));

  // Our saved copy of the PDF. A tab opened at the click is never blocked as a popup; it is pointed at the file when the link arrives.
  const openSavedCertificate = async () => {
    const tab = window.open("", "_blank");
    const res = await getBriskV2DownloadUrl(success.id);
    if (res.error || !res.data || !isSafeLink(res.data.url)) {
      if (tab) tab.close();
      ToastError(res.message || "The certificate PDF is not available right now");
      return;
    }
    if (tab) tab.location.href = res.data.url;
    else window.open(res.data.url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container className="Master_Header_Container" sx={{ px: { xs: 1, sm: 2 } }}>
          <Grid item xs={12} sm={6}>
            <Typography className="Master_Header_Heading" sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}>
              {isUpdate ? "Brisk Edit Certificate" : "Brisk Create Certificate"}
              {config && config.apiEnv === "TEST" && (
                <Chip size="small" color="warning" label="TEST" sx={{ ml: 1, verticalAlign: "middle" }} />
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} />
        </Grid>
      </Box>
      <Box className="op-form-page" sx={{ px: { xs: 0.5, sm: 2 }, pt: 2, pb: 1 }}>
        {configError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {configError}
          </Alert>
        )}
        {listsError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {listsError}
          </Alert>
        )}
        {config && !can.create && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Your login can only view Brisk certificates
          </Alert>
        )}

        {/* the room is always there, so the page does not jump when the bar shows */}
        <Box sx={{ height: 3, mb: 0.5 }}>{reading && <LinearProgress aria-label="Reading the details" sx={{ height: 3, borderRadius: 2 }} />}</Box>
        <Formik
          initialValues={initialValues}
          validationSchema={buildSchema()}
          innerRef={formRef}
          onSubmit={onSubmit}
        >
          {({ errors, touched, values, setFieldValue }) => {
            const split = premiumSplit(values);
            const makeKnown = !values.Make || lists.makes.includes(values.Make);
            const modelKnown = !values.Model || models.includes(values.Model);
            return (
              <Form className="op-form op-policy" ref={formElRef}>
                {/* a Create / Save refused by the form's checks scrolls to the first field with an error (owner, 2026-11-04) */}
                <ScrollToFirstError />
                {!isUpdate && (
                  <FormSection title="Certificate for" testId="brisk-certificate-for">
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, mb: 2 }}>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={mode}
                        aria-label="New policy or already created policy"
                        onChange={(e, next) => {
                          if (next) switchMode(next);
                        }}
                        sx={CHOICE_TOGGLE_SX}
                      >
                        <ToggleButton value={MODE_NEW}>New policy</ToggleButton>
                        <ToggleButton value={MODE_POLICY}>Already created policy</ToggleButton>
                      </ToggleButtonGroup>
                      <Typography sx={hintSx}>
                        {mode === MODE_NEW ? "A certificate on its own, charged to the Brisk wallet." : "A policy whose Brisk PA failed. Its details and PA amount come from the policy."}
                      </Typography>
                    </Box>
                    {mode === MODE_POLICY && (
                      <Grid container columnSpacing={3}>
                        <Grid item xs={12} sm={8}>
                          <Label>Online Policy</Label>
                          <Autocomplete
                            className="AutoComplete_InputBox"
                            options={pendingPolicies}
                            getOptionLabel={(p) => (p ? policyOptionLabel(p) : "")}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            value={selectedPolicy ? pendingPolicies.find((p) => p.id === selectedPolicy.id) || selectedPolicy : null}
                            onChange={(e, value) => choosePolicy(value ? value.id : null)}
                            noOptionsText="No policy has a failed Brisk PA"
                            renderInput={(params) => <TextField {...params} placeholder="Choose a policy whose Brisk PA failed" />}
                          />
                          <div className="errorMessage mb-2 mt-2">{policyError || " "}</div>
                        </Grid>
                      </Grid>
                    )}
                  </FormSection>
                )}
                <FormSection title="Customer Details">
                <Grid container columnSpacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Label>Customer Name</Label>
                    <Field className="textField w-100" name="CustomerName" onChange={(e) => setFieldValue("CustomerName", e.target.value.toUpperCase())} />
                    <FieldError name="CustomerName" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Date of Birth</Label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        name="CustomerDOB"
                        className="Date_Picker w-100"
                        placeholder="Select Date or Enter DD/MM/YYYY"
                        value={fillDOB}
                        onChange={(e) => {
                          setFieldValue("CustomerDOB", e ? e.$d : "");
                          setFillDOB(e);
                        }}
                        format={"DD/MM/YYYY"}
                        placement="bottomRight"
                      />
                    </LocalizationProvider>
                    <FieldError name="CustomerDOB" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Gender</Label>
                    <Autocomplete
                      className="AutoComplete_InputBox"
                      options={toOptions(lists.genders)}
                      isOptionEqualToValue={(option, value) => option?.value === value}
                      clearIcon={false}
                      value={values.CustomerGender}
                      onChange={(e, value) => setFieldValue("CustomerGender", value?.label || "")}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    <FieldError name="CustomerGender" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Mobile</Label>
                    <Field className="textField w-100" name="MobileNo" />
                    <FieldError name="MobileNo" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Email</Label>
                    <Field className="textField w-100" name="EmailID" />
                    <FieldError name="EmailID" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Address Line 1</Label>
                    <Field className="textField w-100" name="Address_Line1" onChange={(e) => setFieldValue("Address_Line1", e.target.value.toUpperCase())} />
                    <FieldError name="Address_Line1" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Address Line 2</Label>
                    <Field className="textField w-100" name="Address_Line2" onChange={(e) => setFieldValue("Address_Line2", e.target.value.toUpperCase())} />
                    <FieldError name="Address_Line2" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>City</Label>
                    <Field className="textField w-100" name="City" onChange={(e) => setFieldValue("City", e.target.value.toUpperCase())} />
                    <FieldError name="City" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>State</Label>
                    <Field
                      className="textField w-100"
                      name="State"
                      disabled={lockedInUpdate("State")}
                      onChange={(e) => setFieldValue("State", e.target.value.toUpperCase())}
                    />
                    <FieldError name="State" errors={errors} touched={touched} />
                  </Grid>
                </Grid>
                </FormSection>
                <FormSection title="Nominee Details">
                <Grid container columnSpacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Label>Nominee Name</Label>
                    <Field className="textField w-100" name="NomineeName" onChange={(e) => setFieldValue("NomineeName", e.target.value.toUpperCase())} />
                    <FieldError name="NomineeName" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Nominee Gender</Label>
                    <Autocomplete
                      className="AutoComplete_InputBox"
                      options={toOptions(lists.genders)}
                      isOptionEqualToValue={(option, value) => option?.value === value}
                      clearIcon={false}
                      value={values.NomineeGender}
                      onChange={(e, value) => setFieldValue("NomineeGender", value?.label || "")}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    <FieldError name="NomineeGender" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Nominee Relationship</Label>
                    <Autocomplete
                      className="AutoComplete_InputBox"
                      options={toOptions(lists.relations)}
                      isOptionEqualToValue={(option, value) => option?.value === value}
                      clearIcon={false}
                      value={values.Relation}
                      onChange={(e, value) => setFieldValue("Relation", value?.label || "")}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    <FieldError name="Relation" errors={errors} touched={touched} />
                  </Grid>
                </Grid>
                </FormSection>
                <FormSection title="Vehicle Details">
                <Grid container columnSpacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Label>Make</Label>
                    <Autocomplete
                      className="AutoComplete_InputBox"
                      options={toOptions(lists.makes)}
                      isOptionEqualToValue={(option, value) => option?.value === value}
                      clearIcon={false}
                      value={values.Make}
                      onChange={(e, value) => {
                        setFieldValue("Make", value?.label || "");
                        setFieldValue("Model", "");
                        loadModels(value?.label);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    {!makeKnown && <small style={{ color: "#b54708" }}>From the policy, {values.Make}. Please pick Brisk's make</small>}
                    <FieldError name="Make" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Model</Label>
                    <Autocomplete
                      className="AutoComplete_InputBox"
                      options={toOptions(models)}
                      isOptionEqualToValue={(option, value) => option?.value === value}
                      clearIcon={false}
                      value={values.Model}
                      onChange={(e, value) => setFieldValue("Model", value?.label || "")}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    {!modelKnown && <small style={{ color: "#b54708" }}>From the policy, {values.Model}. Please pick Brisk's model</small>}
                    <FieldError name="Model" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Registration Number</Label>
                    <Field name="RegistrationNo" type="text" className="textField w-100" />
                    <FieldError name="RegistrationNo" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Chassis Number</Label>
                    <Field name="ChassisNo" type="text" className="textField w-100" disabled={lockedInUpdate("ChassisNo")} />
                    <FieldError name="ChassisNo" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Engine Number</Label>
                    <Field name="EngineNo" type="text" className="textField w-100" disabled={lockedInUpdate("EngineNo")} />
                    <FieldError name="EngineNo" errors={errors} touched={touched} />
                  </Grid>
                </Grid>
                </FormSection>
                <FormSection title="Premium Details">
                <Grid container columnSpacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Label>Policy Type</Label>
                    <Autocomplete
                      className="AutoComplete_InputBox"
                      options={POLICY_TYPE_DETAILS}
                      clearIcon={false}
                      disabled={lockedInUpdate("policyType")}
                      value={values.policyType}
                      onChange={(e, value) => setFieldValue("policyType", value?.label || "")}
                      isOptionEqualToValue={(option, value) => option.value === value}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    <FieldError name="policyType" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Select Plan</Label>
                    <Autocomplete
                      className="AutoComplete_InputBox"
                      options={(lists.plans || []).map((p) => ({ label: p.name, value: p.name, price: p.price }))}
                      isOptionEqualToValue={(option, value) => option?.value === value}
                      clearIcon={false}
                      disabled={lockedInUpdate("PlanName")}
                      value={values.PlanName}
                      onChange={(e, value) => {
                        setFieldValue("PlanName", value?.label || "");
                        if (value?.price && !premiumFromPolicy) setFieldValue("totalPremium", String(Number(value.price)));
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    <FieldError name="PlanName" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Total Premium</Label>
                    <Field name="totalPremium" type="number" className="textField w-100" disabled={lockedInUpdate("totalPremium") || premiumFromPolicy} />
                    {premiumFromPolicy && (
                      <Typography component="small" sx={{ ...hintSx, display: "block", mt: 0.5 }}>
                        {selectedPolicy ? `The PA amount of policy ${selectedPolicy.policyNo}. It cannot be changed` : "Comes from the policy you choose"}
                      </Typography>
                    )}
                    <FieldError name="totalPremium" errors={errors} touched={touched} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Net Premium</Label>
                    <input className="textField w-100" name="netPremium" type="number" value={split.net} readOnly style={{ backgroundColor: "#F9FAFB", color: "#667085" }} />
                    {values.totalPremium !== "" && (
                      <small style={{ color: NAVY, display: "block", fontFamily: FONT, marginTop: 4 }}>
                        Net Premium: {split.net}, GST: {split.gst}
                      </small>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Label>Payment Mode</Label>
                    <Autocomplete
                      className="AutoComplete_InputBox"
                      options={PAYMENT_DETAILS}
                      clearIcon={false}
                      disabled={lockedInUpdate("PaymentMode")}
                      value={values.PaymentMode}
                      onChange={(e, value) => setFieldValue("PaymentMode", value?.value || "")}
                      isOptionEqualToValue={(option, value) => option.value === value}
                      getOptionLabel={(option) => (typeof option === "string" ? (PAYMENT_DETAILS.find((p) => p.value === option) || {}).label || option : option.label)}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    <FieldError name="PaymentMode" errors={errors} touched={touched} />
                  </Grid>
                </Grid>

                {!isUpdate && (
                  <Grid container spacing={1} alignItems="center" sx={{ mt: 0, mb: 1.5 }}>
                    {mode === MODE_NEW && (
                      <Grid item xs={12} sm="auto">
                        <Box sx={BALANCE_SX}>
                          <AccountBalanceWalletIcon />
                          <span>Wallet Balance = {balance === null ? "not available" : formatINR(balance)}</span>
                        </Box>
                      </Grid>
                    )}
                    {/* a user login pays from his own Policy Wallet too (owner, 2026-11-03); the server sends it only for him */}
                    {mode === MODE_NEW && config && config.policyWallet && (
                      <Grid item xs={12} sm="auto">
                        <Tooltip title="A user login pays from his own Policy Wallet too, like his Online Policies">
                          <Box sx={BALANCE_SX} data-testid="policy-wallet-balance">
                            <AccountBalanceWalletIcon />
                            <span>Your Policy Wallet = {config.policyWallet.balance === null ? "not available" : formatINR(config.policyWallet.balance)}</span>
                          </Box>
                        </Tooltip>
                      </Grid>
                    )}
                    {can.seeDealerBalance && (
                      <Grid item xs={12} sm="auto">
                        <Tooltip title="Brisk's own wallet: one Brisk account shared by every agency. Brisk takes each certificate from it">
                          <Box sx={BALANCE_SX}>
                            <AccountBalanceOutlined />
                            <span>Brisk Dealer Wallet = {dealerBalance === null ? "not available" : formatINR(dealerBalance)}</span>
                          </Box>
                        </Tooltip>
                      </Grid>
                    )}
                    {(mode === MODE_NEW || can.seeDealerBalance) && (
                      <Grid item xs={12} sm="auto">
                        <Tooltip title="Refresh Balance">
                          <IconButton onClick={refreshBalances} aria-label="refresh" sx={{ color: NAVY }}>
                            <RefreshOutlined />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    )}
                    {mode === MODE_POLICY && selectedPolicy && (
                      <Grid item xs={12}>
                        {selectedPolicy.chargedAgain ? (
                          <Alert severity="warning">
                            Policy {selectedPolicy.policyNo}'s earlier certificate was cancelled and its money refunded, so this certificate is charged to the Brisk wallet.
                          </Alert>
                        ) : (
                          <Alert severity="info">
                            Paid with policy {selectedPolicy.policyNo} (PA ₹{selectedPolicy.paAmount}
                            {Number(selectedPolicy.briskCharged) > 0
                              ? `; the Brisk wallet took ₹${selectedPolicy.briskCharged} when the policy was created`
                              : ""}
                            ). Nothing will be charged.
                          </Alert>
                        )}
                      </Grid>
                    )}
                  </Grid>
                )}
                {newBlockedForSuperAdmin && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    The super admin does not have a Company Wallet. Choose Already created policy, or log in as the admin of the client
                  </Alert>
                )}
                </FormSection>
                {/* the buttons stay at the bottom of the window while the page scrolls (.op-action-bar in onlinePolicyForm.scss) */}
                <Box className="op-action-bar" data-testid="form-action-bar">
                  <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexWrap: "wrap" }}>
                    {(sendingAction === "create" || sendingAction === "update") && (
                      <Typography role="status" sx={{ ...hintSx, fontSize: 12.5, mr: "auto" }}>
                        {sendingAction === "create" ? "Asking Brisk to create the certificate. This can take up to a minute." : "Asking Brisk to update the certificate. This can take up to a minute."}
                      </Typography>
                    )}
                    {/* a user login has no drafts (owner, 2026-11-03): the server says so with can.saveDraft */}
                    {!isUpdate && can.saveDraft !== false && (
                      <Button
                        onClick={() => {
                          actionRef.current = "draft";
                        }}
                        variant="outlined"
                        size="small"
                        type="submit"
                        disabled={sending || reading || readOnly}
                        startIcon={sendingAction === "draft" ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={{ ...SECONDARY_BUTTON_SX, ...BAR_BUTTON_SX }}
                      >
                        Save Draft
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        actionRef.current = isUpdate ? "update" : "create";
                      }}
                      variant="contained"
                      size="small"
                      type="submit"
                      disabled={sending || reading || readOnly || Boolean(listsError) || newBlockedForSuperAdmin}
                      startIcon={sendingAction === "create" || sendingAction === "update" ? <CircularProgress size={14} color="inherit" /> : null}
                      sx={{ ...PRIMARY_BUTTON_SX, ...BAR_BUTTON_SX }}
                    >
                      {isUpdate ? "Update Certificate" : "Create Certificate"}
                    </Button>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>

      {success && (
        <DialogFrame
          title="Certificate Created"
          subtitle={success.certificateNo ? `Certificate ${success.certificateNo}` : ""}
          onClose={() => setSuccess(null)}
          footer={
            <>
              <Button variant="outlined" sx={{ textTransform: "none", borderRadius: "6px" }} onClick={() => navigate(BRISK_V2_LIST_PATH)}>
                Open Certificate List
              </Button>
              <Button variant="outlined" sx={{ textTransform: "none", borderRadius: "6px" }} onClick={startAnother}>
                Create Another
              </Button>
            </>
          }
        >
          <Typography sx={{ fontSize: 13.5, color: "#344054", mb: success.url ? 2 : 0 }}>{success.message}</Typography>
          {success.url && (
            <Button
              variant="contained"
              disableElevation
              onClick={openSavedCertificate}
              startIcon={<DownloadOutlined />}
              sx={{ textTransform: "none", borderRadius: "6px", alignSelf: "flex-start" }}
            >
              Download Certificate
            </Button>
          )}
        </DialogFrame>
      )}
    </>
  );
};

export default BriskCreateCertificateV2;
